import { API } from 'api'
import {
	EditDynamicListOptionsResponse,
	EditStaticListOptionsResponse,
	ExportResponse,
	GetPeopleTagsDeleteOrgResponse,
	GetPeopleTagsOrgResponse,
	PeopleMyFilterResponse,
	PeopleMyListsResponse,
	PeopleMyPeopleResponse,
	PeopleMyTagsChildrenResponse,
	PeopleMyTagsParentResponse,
	PeopleMyTagsResponse,
	TagsApprovalResponse,
	TagsUserRequest,
	TotalFollowersResponse,
	TypeOrder,
	UpdateImageResponse,
	UserInfoResponse,
	UsersPlatformResponse
} from 'interfaces'
import { captureErrorSentry, SentryEvents, SentrySections } from 'utils/sentry'
import { CSVImportLogResponse } from './people.type'

/**
 * Fetches the My People table data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param filters - Active filters serialized as string
 * @param size - Number of results per page (default: 10)
 * @returns Promise resolving to PeopleMyPeopleResponse or null
 */
export const getPeopleMyPeopleTable = async (
	organizationId: number,
	page: number,
	searchValue: string,
	column?: string,
	orderBy?: TypeOrder,
	filters?: string,
	size = 10
): Promise<PeopleMyPeopleResponse | null> => {
	let res = null
	await API()
		.get(`/people/people_list/${organizationId}`, {
			params: {
				size,
				page,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined,
				filters: filters || undefined,
				list_pending_tags: true
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				captureErrorSentry({
					section: SentrySections.PEOPLE,
					event: SentryEvents.GET_PEOPLE_MY_PEOPLE_TABLE_ERROR,
					error,
					extra: {
						organizationId,
						params: {
							size,
							page,
							search: searchValue,
							column_order: column,
							order_by: orderBy,
							filters,
							list_pending_tags: true
						}
					}
				})
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Exports My People data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param filters - Active filters serialized as string
 * @param resourceId - Resource ID to filter by
 * @param resourceType - Resource type to filter by
 * @param idUser - List of organization follower IDs to export
 * @returns Promise resolving to ExportResponse or null
 */
export const getExportMyPeople = async (
	organizationId: number,
	page: number,
	searchValue?: string,
	column?: string,
	orderBy?: TypeOrder,
	filters?: string,
	resourceId?: number,
	resourceType?: string,
	idUser?: number[]
): Promise<ExportResponse | null> => {
	let res = null
	await API()
		.get(`/people/export_people_data/${organizationId}`, {
			params: {
				page,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined,
				filters: filters || undefined,
				organization_follower_ids: idUser?.join(','),
				list_pending_tags: false,
				resource_id: resourceId,
				resource_type: resourceType || undefined
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
				throw error
			}
		)
	return res
}

/**
 * Fetches the My Lists table data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param size - Number of results per page
 * @returns Promise resolving to PeopleMyListsResponse or null
 */
export const getPeopleMyListsTable = async (
	organizationId: number,
	page: number,
	searchValue: string,
	column?: string,
	orderBy?: TypeOrder,
	size?: number
): Promise<PeopleMyListsResponse | null> => {
	let res = null
	await API()
		.get(`/people/organization/${organizationId}`, {
			params: {
				size: size ?? 10,
				page,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				captureErrorSentry({
					section: SentrySections.PEOPLE,
					event: SentryEvents.GET_PEOPLE_MY_LISTS_TABLE_ERROR,
					error,
					extra: {
						organizationId,
						params: {
							size: size ?? 10,
							page,
							search: searchValue,
							column_order: column,
							order_by: orderBy
						}
					}
				})
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the People Tags (parent) table data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param size - Number of results per page
 * @param all - When true, fetches all records without pagination
 * @returns Promise resolving to PeopleMyTagsParentResponse or null
 */
export const getPeopleTagsTable = async (
	organizationId: number,
	page: number,
	searchValue: string,
	column?: string,
	orderBy?: TypeOrder,
	size?: number,
	all = false
): Promise<PeopleMyTagsParentResponse | null> => {
	let res = null
	await API()
		.get(`/people/tags/parent/${organizationId}`, {
			params: {
				not_paginate: all || undefined,
				size: all ? undefined : size ?? 10,
				page: all ? undefined : page,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				captureErrorSentry({
					section: SentrySections.PEOPLE,
					event: SentryEvents.GET_PEOPLE_TAGS_TABLE_ERROR,
					error,
					extra: {
						organizationId,
						params: {
							not_paginate: all || undefined,
							size: all ? undefined : size ?? 10,
							page: all ? undefined : page,
							search: searchValue,
							column_order: column,
							order_by: orderBy
						}
					}
				})
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches subtags for a given parent tag
 * @param id - Parent tag ID
 * @returns Promise resolving to PeopleMyTagsChildrenResponse or null
 */
export const getPeopleSubtagsTable = async (
	id: number | null
): Promise<PeopleMyTagsChildrenResponse | null> => {
	let res = null
	if (id) {
		await API()
			.get(`/people/tags/sub_tags/${id.toString()}`, {
				params: { size: 1000 }
			})
			.then(
				async response => {
					res = response.data
				},
				error => {
					console.warn('Error:', error.message)
				}
			)
	}
	return res
}

/**
 * Fetches all independent child tags for an organization
 * @param organizationId - The organization ID
 * @returns Promise resolving to PeopleMyTagsResponse or null
 */
export const getPeopleTag = async (
	organizationId: number
): Promise<PeopleMyTagsResponse | null> => {
	let res = null
	if (organizationId) {
		await API()
			.get(`/people/tags/child_independent/${organizationId}`, {
				params: { not_paginate: true }
			})
			.then(
				async response => {
					res = response.data
				},
				error => {
					console.warn('Error:', error.message)
				}
			)
	}
	return res
}

/**
 * Fetches filter configuration for a dynamic list
 * @param organizationId - The organization ID
 * @param listId - The dynamic list ID
 * @returns Promise resolving to PeopleMyFilterResponse or null
 */
export const getListFiltersId = async (
	organizationId: number,
	listId: number
): Promise<PeopleMyFilterResponse | null> => {
	let res = null
	if (organizationId && listId) {
		await API()
			.get(`/people/dynamic_list/${listId}/organization/${organizationId}`)
			.then(
				async response => {
					res = response.data
				},
				error => {
					console.warn('Error:', error.message)
				}
			)
	}
	return res
}

/**
 * Fetches the total follower count for an organization
 * @param organizationId - The organization ID
 * @returns Promise resolving to TotalFollowersResponse or null
 */
export const getTotalFollowers = async (
	organizationId: number
): Promise<TotalFollowersResponse | null> => {
	let res = null
	await API()
		.get(`/people/total_followers/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the platform users list for an organization
 * @param organizationId - The organization ID
 * @returns Promise resolving to UsersPlatformResponse or null
 */
export const getUsersPlatform = async (
	organizationId: number
): Promise<UsersPlatformResponse | null> => {
	let res = null
	await API()
		.get(`/people/users_plataform/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the member options for editing a static list
 * @param organizationId - The organization ID
 * @param id - Static list ID
 * @param searchValue - Search query string
 * @returns Promise resolving to EditStaticListOptionsResponse or null
 */
export const getEditStaticListOptions = async (
	organizationId: number,
	id: number,
	searchValue?: string
): Promise<EditStaticListOptionsResponse | null> => {
	let res = null
	await API()
		.get(
			`/people/static_list/${id.toString()}/organization/${organizationId}`,
			{
				params: {
					search: searchValue || undefined
				}
			}
		)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the member options for editing a dynamic list
 * @param organizationId - The organization ID
 * @param id - Dynamic list ID
 * @returns Promise resolving to EditDynamicListOptionsResponse or null
 */
export const getEditDynamicListOptions = async (
	organizationId: number,
	id: number
): Promise<EditDynamicListOptionsResponse | null> => {
	let res = null
	await API()
		.get(`/people/dynamic_list/${id.toString()}/organization/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches all tags for an organization
 * @param organizationId - The organization ID
 * @returns Promise resolving to GetPeopleTagsOrgResponse or null
 */
export const getPeopleTags = async (
	organizationId: number
): Promise<GetPeopleTagsOrgResponse | null> => {
	let res = null
	await API()
		.get(`/people/tags/organization/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches all tags for an organization excluding a specific tag
 * @param organizationId - The organization ID
 * @param id - Tag ID to exclude
 * @param mode - Filter mode
 * @returns Promise resolving to GetPeopleTagsDeleteOrgResponse or null
 */
export const getPeopleTagsExclude = async (
	organizationId: number,
	id: number,
	mode: string
): Promise<GetPeopleTagsDeleteOrgResponse | null> => {
	let res = null
	try {
		const response = await API().get(
			`/people/tags/organization/${organizationId}`,
			{
				params: {
					exclude_tag_id: id,
					mode: mode || undefined
				}
			}
		)
		res = response.data
	} catch (error) {
		console.warn('Error:', error)
	}
	return res
}

/**
 * Fetches all tags for an organization excluding a specific tag (edit mode)
 * @param organizationId - The organization ID
 * @param id - Tag ID to exclude
 * @returns Promise resolving to GetPeopleTagsOrgResponse or null
 */
export const getPeopleTagsEditExclude = async (
	organizationId: number,
	id: number
): Promise<GetPeopleTagsOrgResponse | null> => {
	let res = null
	await API()
		.get(`/people/tags/organization/${organizationId}`, {
			params: { exclude_tag_id: id }
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the organization info
 * @param organizationId - The organization ID
 * @returns Promise resolving to UserInfoResponse or null
 */
export const getOrgInfo = async (
	organizationId: number
): Promise<UserInfoResponse | null> => {
	let res = null
	await API()
		.get(`/organization/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the Tags Approval table data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param size - Number of results per page
 * @returns Promise resolving to TagsApprovalResponse or null
 */
export const getTagsApprovalTable = async (
	organizationId: number,
	page?: number,
	searchValue?: string,
	column?: string,
	orderBy?: TypeOrder,
	size?: number
): Promise<TagsApprovalResponse | null> => {
	let res = null
	await API()
		.get(`/people/tag/review/organization/${organizationId}`, {
			params: {
				size: size ?? 10,
				page: page && page > 0 ? page : undefined,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches a summary of the latest pending tag approvals for the dashboard
 * @param organizationId - The organization ID
 * @returns Promise resolving to TagsApprovalResponse or null
 */
export const getTagsApprovalDashboard = async (
	organizationId: number
): Promise<TagsApprovalResponse | null> => {
	let res = null
	await API()
		.get(`/people/tag/review/organization/${organizationId}`, {
			params: {
				size: 5,
				order_by: 'desc',
				column_order: 'created_at'
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the tags assigned to a user within an organization
 * @param organizationId - The organization ID
 * @param userId - The user ID
 * @returns Promise resolving to TagsUserRequest or null
 */
export const getUserTags = async (
	organizationId: number,
	userId: number | undefined
): Promise<TagsUserRequest | null> => {
	let res = null
	await API()
		.get(`/user/${userId}/tags/organization/${organizationId}`)
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the Batch Edit People table data for an organization
 * @param organizationId - The organization ID
 * @param page - Current page number
 * @param searchValue - Search query string
 * @param column - Column to sort by
 * @param orderBy - Sort direction
 * @param data - Additional raw query string parameters (e.g. from active batch filters)
 * @param size - Number of results per page (default: 10)
 * @returns Promise resolving to PeopleMyPeopleResponse or null
 */
export const getPeopleBatchEditTable = async (
	organizationId: number,
	page: number,
	searchValue: string,
	column?: string,
	orderBy?: TypeOrder,
	data?: string,
	size = 10
): Promise<PeopleMyPeopleResponse | null> => {
	const dataParams = data ? Object.fromEntries(new URLSearchParams(data)) : {}
	let res = null
	await API()
		.get(`people/massive_people_list/${organizationId}`, {
			params: {
				size,
				page,
				search: searchValue || undefined,
				column_order: column || undefined,
				order_by: orderBy || undefined,
				list_pending_tags: false,
				...dataParams
			}
		})
		.then(
			async response => {
				res = response.data
			},
			error => {
				console.warn('Error:', error.message)
			}
		)
	return res
}

/**
 * Fetches the pre-signed URL for uploading a CSV import report
 * @returns Promise resolving to UpdateImageResponse or null
 */
export const getUploadFileCSV =
	async (): Promise<UpdateImageResponse | null> => {
		let res = null as UpdateImageResponse | null
		await API()
			.get(`/upload/people/reports/import`)
			.then(
				async response => {
					res = response.data
				},
				error => {
					console.error(error)
				}
			)

		return res
	}

/**
 * Fetches the CSV import log for an organization
 * @param organizationId - The organization ID
 * @returns Promise resolving to CSVImportLogResponse or null
 */
export const getCSVImportLog = async (
	organizationId: number
): Promise<CSVImportLogResponse | null> => {
	try {
		const response = await API().get<CSVImportLogResponse>(
			`/people/csv_import_log/${organizationId}`
		)
		return response.data
	} catch (error) {
		if (error instanceof Error) {
			console.error('Error fetching CSV import log:', {
				message: error.message,
				organizationId
			})
		} else {
			console.error('Unknown error fetching CSV import log:', error)
		}
		return null
	}
}
